<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="single itinerary">
    <FlightSummary v-model:is-details="isDetails" :is-edit="isEdit" :flight="flight" :flight-way="flightWay" :is-finished="isFinished" :is-lander="isLander" :show-flight-type="!!program?.group_flights" @edit="emit('edit')" />
    <div v-if="isDetails" class="details">
      <div class="items" :class="{ 'no-selection': !shouldShowSelection && !shouldShowDebug }">
        <div v-for="(leg, index) in flight.legs || []" :key="index">
          <Flight :airline-iata="leg.airline_iata" :airline-iata-op="leg.airline_iata_op" :airline-title="leg.airline_title" :airline-title-op="leg.airline_title_op" :airport-depart-iata="leg.airport_depart_iata" :airport-depart-title="leg.airport_depart_title" :airport-arrive-iata="leg.airport_arrive_iata" :airport-arrive-title="leg.airport_arrive_title" :cabin="leg.cabin" :elapsed="leg.elapsed" :is-codeshare="leg.is_codeshare" :number="leg.airline_iata + leg.number" :time-depart="leg.time_depart" :time-arrive="leg.time_arrive" :tag="IsTag(leg)" :is-group-flight="leg.is_group_flight === true" />
          <ItinerarySpacer v-if="index < flight.legs.length - 1" :layover="layover(index)" />
        </div>
      </div>
      <div v-if="shouldShowSelection" class="selection">
        <FlightWarnings v-model:disclaimer-accepted="disclaimer" v-model:unchaperoned-accepted="unchaperoned" :flight="flight" :is-summary="isSummary" :is-lander="isLander" :wants-chaperoned="wantsChaperoned === true" :is-disclaimer-error="isDisclaimerError" />
        <template v-if="isResult">
          <button v-if="isFinished" class="block" :class="{ loading: props.flight.signature === props.validatingSignature }" @click="select">Select</button>
          <button v-else class="block" disabled>Still loading prices...</button>
        </template>
      </div>
      <div v-if="shouldShowDebug" class="debug">
        <div v-for="(p, index) in prices" :key="index" v-html="priceDebug(p)"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, watch, computed } from "vue";
import Flight from "./common/ItineraryFlight.vue";
import FlightSummary from "./common/ItineraryFlightSummary.vue";
import FlightWarnings from "./common/ItineraryFlightWarnings.vue";
import ItinerarySpacer from "./common/ItinerarySpacer.vue";
import DateTime from "../utils/datetime";

const { utilDurationItineraryMinutes } = DateTime();

const props = defineProps(["flight", "flightCabin", "flightWay", "isResult", "policyFlightShortMax", "policyFlightMediumMax", "policyFlightLongMax", "isFinished", "isLander", "wantsChaperoned", "filter", "isDev", "program", "isSummary", "isEdit", "validatingSignature"]);
const emit = defineEmits(["selectFlight", "edit", "toggleDetails"]);

const userIsExpert = inject("userIsExpert");
const groupFlightLabel = inject("groupFlightLabel", ref("Chaperoned"));
const individualFlightLabel = inject("individualFlightLabel", ref("Unchaperoned"));
const prices = ref([]);

const isDetails = ref(false);
const disclaimer = ref(false);
const unchaperoned = ref(false);
const isDisclaimerError = ref(false);

const shouldShowSelection = computed(() => {
  // Show if there are warnings to display
  const hasWarnings = props.flight.disclaimers?.length > 0 || (props.wantsChaperoned && !props.flight.is_group_flight);
  // Show if there are buttons to display
  const hasButtons = props.isResult;

  return hasWarnings || hasButtons;
});

const shouldShowDebug = computed(() => (props.isDev || userIsExpert.value) && prices.value.length > 0);

watch(
  isDetails,
  () => {
    emit("toggleDetails", isDetails.value);
    if (isDetails.value && prices.value.length == 0 && props.flight.prices) {
      prices.value = props.flight.prices;
      prices.value.sort((a, b) => a.amount - b.amount);
    }
  },
  { immediate: true }
);

const IsTag = leg => {
  // Backend provides is_group_flight as *bool - only set for lander searches when is_flight_type_shown is true
  if (leg.is_group_flight === undefined || leg.is_group_flight === null) return null;
  return leg.is_group_flight ? groupFlightLabel.value : individualFlightLabel.value;
};

const layover = index => {
  const schedule = props.flight.legs;
  if (schedule && index < schedule.length - 1) {
    const flight1Arrive = new Date(props.flight.legs[index].time_arrive);
    const flight2Depart = new Date(props.flight.legs[index + 1].time_depart);
    var minutes = (flight2Depart - flight1Arrive) / 1000 / 60;

    return utilDurationItineraryMinutes(minutes);
  }
  return "";
};

const priceDebug = p => {
  return `${p.amount} ${p.fare ? p.fare : "Unknown"} (${p.source}) ${p.signature}`;
};

const select = () => {
  if (!props.isFinished && props.flight.price_display.amount == 0) {
    return;
  }

  const confirmChaperoned = props.isLander && props.wantsChaperoned === true && !props.flight.is_group_flight && !unchaperoned.value;
  const confirmRedFlagged = props.isLander && props.flight.disclaimers?.length > 0 && !disclaimer.value;

  // console.log("wantsChaperoned", props.wantsChaperoned);
  // console.log("confirmChaperoned", confirmChaperoned);
  // console.log("confirmRedFlagged", confirmRedFlagged);

  if (confirmChaperoned || confirmRedFlagged) {
    isDisclaimerError.value = true;

    // Remove error state after a short delay
    setTimeout(() => {
      isDisclaimerError.value = false;
    }, 500);
    return;
  }

  emit("selectFlight", props.flight);
};
</script>

<style lang="less">
#flights {
  .single.disabled {
    .title,
    .summ {
      cursor: not-allowed;
    }
  }

  .single {
    background: @lighter;
    border: 0.0625rem solid @border;
    margin-bottom: 1rem;

    &:hover {
      border-color: @primary;
    }

    .title {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 1rem;
      background: @light;
      border-bottom: 1px solid @white;
      border-radius: 1rem 1rem 0 0;
      box-sizing: border-box;
      cursor: pointer;
    }

    .details {
      border-top: 0.0625rem solid @light;
    }

    .items {
      &.no-selection {
        border-radius: 0 0 1rem 1rem;
      }
    }

    .debug {
      border-radius: 0 0 1rem 1rem;
      overflow-x: auto;
      padding: 1rem;
      white-space: nowrap;
    }

    .items + .debug {
      border-top: 0.0625rem solid @light;
    }

    .selection + .debug {
      padding-top: 0;
    }
  }

  .selection {
    border-top: 0.0625rem solid @light;
    padding: 1rem;
  }
}

@media screen and (min-width: 60em) {
  #flights {
    .single .summ {
      .max {
        display: block;
      }

      .collogo {
        display: flex;
        margin-right: 2rem;

        .logos {
          align-self: center;
          height: 2.75rem;
          position: relative;
          width: 2.75rem;

          img {
            display: block;
            width: 2.75rem;
          }

          &.multi {
            img {
              position: absolute;
              width: 1.25rem;
            }

            img:nth-child(1) {
              left: 0;
              top: 0;
            }

            img:nth-child(2) {
              bottom: 0;
              right: 0;
            }

            img:nth-child(3) {
              bottom: 0;
              left: 0;
            }

            img:nth-child(4) {
              right: 0;
              top: 0;
            }
          }
        }
      }

      .col3s {
        margin-left: 4rem;
      }
    }
  }
}
</style>
